/**
 * GlobalLoadingProvider — P0 Sprint (28 Feb 2026, Iteratie A).
 *
 * Één centrale bron voor loading-overlays bij expliciete gebruikersacties.
 *
 * WEL loading-overlay: PDF genereren, factuur verzenden, herinnering,
 * offerte, manifest, export, e-mail. Acties die merkbaar langer duren
 * (>300ms) verdienen visuele feedback en dubbele-submit-blokkering.
 *
 * NIET loading-overlay: gewone lijst-loads, achtergrondrefreshes. Die
 * gebruiken inline skeletons/spinners in hun eigen component.
 *
 * Gebruik:
 *
 *     import { useGlobalLoading } from ".../GlobalLoadingProvider";
 *
 *     function MyComponent() {
 *       const { runTask } = useGlobalLoading();
 *       const handleDownload = async () => {
 *         await runTask({
 *           id: "wr-pdf-{shipment.id}",
 *           message: "Warehouse Receipt wordt gegenereerd…",
 *           successMessage: "Warehouse Receipt klaar",
 *           run: async () => {
 *             const r = await adminApi.get(url, { responseType: "blob" });
 *             saveAs(r.data, "wr.pdf");
 *           },
 *         });
 *       };
 *     }
 *
 * `id` is optioneel maar wordt gebruikt voor dubbele-submit-detectie —
 * als dezelfde `id` al bezig is, wordt de tweede call afgewezen.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState,
} from "react";
import { Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";

const GlobalLoadingContext = createContext(null);
const OVERLAY_DELAY_MS = 300;
const SUCCESS_HOLD_MS = 900;
const ERROR_HOLD_MS = 2400;

export function GlobalLoadingProvider({ children }) {
  // Map van actieve tasks: id → { message, startedAt, state, error }
  const [tasks, setTasks] = useState({});
  // Wanneer meerdere tasks parallel lopen: we tonen de nieuwste als headline.
  const activeIds = Object.keys(tasks);
  const activeCount = activeIds.filter(
    (id) => tasks[id].state === "running",
  ).length;

  const upsert = useCallback((id, patch) => {
    setTasks((prev) => {
      const cur = prev[id] || {};
      return { ...prev, [id]: { ...cur, ...patch } };
    });
  }, []);

  const remove = useCallback((id) => {
    setTasks((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /**
   * Start een task.
   *
   * @param {Object} opts
   * @param {string} [opts.id]                — dedup-key
   * @param {string} opts.message             — contextuele melding
   * @param {string} [opts.subMessage]        — secundaire regel (bv voortgangs-stap)
   * @param {string} [opts.successMessage]    — kort NL bericht bij succes
   * @param {string} [opts.errorMessage]      — override foutmelding
   * @param {(update: (msg: string, sub?: string) => void) => Promise<any>} opts.run
   *          — async werk. Krijgt een callback om de UI-message live te
   *            updaten (voor gestapelde progressie).
   * @returns {Promise<any>} — waarde van run() of throwt de originele fout
   */
  const runTask = useCallback(async (opts) => {
    const id = opts.id || `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    if (tasks[id]?.state === "running") {
      throw new Error("DUPLICATE_TASK");
    }
    upsert(id, {
      id, message: opts.message || "Even geduld…",
      subMessage: opts.subMessage || "",
      state: "running",
      startedAt: Date.now(),
    });
    const update = (msg, sub) => {
      upsert(id, {
        message: msg || opts.message,
        subMessage: sub !== undefined ? sub : "",
      });
    };
    try {
      const result = await opts.run(update);
      upsert(id, { state: "success",
                    message: opts.successMessage || opts.message,
                    subMessage: "" });
      setTimeout(() => remove(id), SUCCESS_HOLD_MS);
      return result;
    } catch (err) {
      const msg = opts.errorMessage
                    || err?.response?.data?.detail
                    || err?.message
                    || "Actie mislukt";
      upsert(id, { state: "error", message: msg, subMessage: "", error: err });
      setTimeout(() => remove(id), ERROR_HOLD_MS);
      throw err;
    }
  }, [tasks, upsert, remove]);

  const dismiss = useCallback((id) => remove(id), [remove]);

  const value = useMemo(() => ({
    runTask,
    activeCount,
    dismiss,
    tasks,
  }), [runTask, activeCount, dismiss, tasks]);

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
      <GlobalLoadingOverlay tasks={tasks} onDismiss={dismiss} />
    </GlobalLoadingContext.Provider>
  );
}

/** Hook: pas op — moet binnen GlobalLoadingProvider gebruikt worden. */
export function useGlobalLoading() {
  const ctx = useContext(GlobalLoadingContext);
  if (!ctx) {
    // Fail-soft: als provider ontbreekt, run() als passthrough zodat
    // callers niet crashen (bijv. tests / storybook).
    return {
      runTask: async (opts) => opts.run?.(),
      activeCount: 0,
      dismiss: () => {},
      tasks: {},
    };
  }
  return ctx;
}

// ═══════════════════════════════════════════════════════════════
// Overlay presentation
// ═══════════════════════════════════════════════════════════════

function GlobalLoadingOverlay({ tasks, onDismiss }) {
  const [visibleAt, setVisibleAt] = useState({}); // id → visibleAt timestamp
  const timerRef = useRef({});

  // Delay visibility per taak: >300ms voordat overlay verschijnt
  useEffect(() => {
    Object.entries(tasks).forEach(([id, t]) => {
      if (t.state === "running" && !timerRef.current[id]) {
        timerRef.current[id] = setTimeout(() => {
          setVisibleAt((v) => ({ ...v, [id]: Date.now() }));
        }, OVERLAY_DELAY_MS);
      } else if (t.state !== "running") {
        // Fase succes/error → toon meteen zichtbaar
        if (!visibleAt[id]) {
          setVisibleAt((v) => ({ ...v, [id]: Date.now() }));
        }
      }
    });
    // cleanup verwijderde tasks
    Object.keys(timerRef.current).forEach((id) => {
      if (!tasks[id]) {
        clearTimeout(timerRef.current[id]);
        delete timerRef.current[id];
        setVisibleAt((v) => {
          const nx = { ...v };
          delete nx[id];
          return nx;
        });
      }
    });
  }, [tasks, visibleAt]);

  const showList = Object.entries(tasks).filter(
    ([id, t]) => t.state !== "running" || visibleAt[id],
  );
  if (showList.length === 0) return null;

  return (
    <div
      className="fixed inset-x-0 top-4 z-[100] flex flex-col items-center pointer-events-none gap-2"
      data-testid="global-loading-overlay"
    >
      {showList.map(([id, t]) => (
        <div
          key={id}
          className={`pointer-events-auto min-w-[280px] max-w-[520px] px-4 py-3 shadow-lg border flex items-center gap-3 text-sm bg-white ${
            t.state === "success" ? "border-emerald-200"
            : t.state === "error" ? "border-red-200"
            : "border-slate-300"
          }`}
          data-testid={`global-loading-task-${id}`}
          data-state={t.state}
        >
          <div className="flex-shrink-0">
            {t.state === "running" && <Loader2 className="w-5 h-5 animate-spin text-[#082c50]" />}
            {t.state === "success" && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
            {t.state === "error" && <AlertTriangle className="w-5 h-5 text-red-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-bold uppercase tracking-wide ${
              t.state === "error" ? "text-red-800"
              : t.state === "success" ? "text-emerald-800"
              : "text-slate-700"
            }`}>
              {t.state === "running" ? "Bezig" : t.state === "success" ? "Klaar" : "Fout"}
            </div>
            <div className="text-sm text-slate-900 font-medium truncate" data-testid={`global-loading-message-${id}`}>
              {t.message}
            </div>
            {t.subMessage ? (
              <div className="text-[11px] text-slate-500 mt-0.5 truncate"
                    data-testid={`global-loading-sub-${id}`}>
                {t.subMessage}
              </div>
            ) : null}
          </div>
          {t.state !== "running" && (
            <button
              type="button"
              onClick={() => onDismiss(id)}
              className="flex-shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Sluiten"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
